import { RefObject, useEffect, useState } from 'react';
import { debounce, throttle } from 'underscore';
import globalWindowSize from './global-window-size';

type TUseTrackResize = {
  ref?: RefObject<HTMLElement | null> | HTMLElement | null;
  trackWindow?: boolean;
  trackType?: 'debounced' | 'throttled' | 'debounced_and_throttled';
  debounceTime?: number;
  throttleTime?: number;
};

const useTrackResize = (props?: TUseTrackResize) => {
  const { ref, trackType = 'fast', debounceTime = 50, throttleTime = 50, trackWindow = true } = props ?? {};
  const initEl = ref instanceof HTMLElement ? ref : ref?.current;
  const [width, setWidth] = useState(trackWindow ? globalWindowSize.innerWidth : (initEl?.offsetWidth ?? 0));
  const [height, setHeight] = useState(trackWindow ? globalWindowSize.innerHeight : (initEl?.offsetHeight ?? 0));

  useEffect(() => {
    const el = ref instanceof HTMLElement ? ref : ref?.current;

    if (!trackWindow && !el) {
      return;
    }

    const globalListeners = { setWidth, setHeight };

    const handleResize = () => {
      if (el) {
        setWidth(el.offsetWidth);
        setHeight(el.offsetHeight);
      }
    };

    handleResize();

    const debounced = debounce(handleResize, debounceTime);
    const throttled = throttle(handleResize, throttleTime);

    const trackDebounced = trackType === 'debounced' || trackType === 'debounced_and_throttled';
    const trackThrottled = trackType === 'throttled' || trackType === 'debounced_and_throttled';

    if (trackWindow) {
      globalWindowSize.addEventListener(globalListeners);
      globalWindowSize.update(globalListeners);
    }

    let resizeObserver: ResizeObserver | undefined;
    if (el) {
      resizeObserver = new ResizeObserver(() => {
        if (trackDebounced) debounced();
        if (trackThrottled) throttled();
        if (!trackDebounced && !trackThrottled) handleResize();
      });
      resizeObserver.observe(el);
    }

    return () => {
      resizeObserver?.disconnect();
      debounced.cancel();
      throttled.cancel();
      globalWindowSize.removeEventListener(globalListeners);
    };
  }, [debounceTime, ref, throttleTime, trackType, trackWindow]);

  return { width, height };
};

export default useTrackResize;
